/**
 * Streama CMS · IAM TanStack Query layer
 *
 * Centralises three things that are easy to drift if scattered:
 *
 *   1. Cache keys (iamKeys) — hierarchical tuples so invalidation
 *      can be coarse (`iamKeys.users()` invalidates list AND every
 *      detail page) or fine (`iamKeys.userPermissions(userName)`).
 *
 *   2. Read hooks (useUsersQuery, etc) — wrap useQuery with sane
 *      IAM defaults: 60-second stale time for the list (admin sets
 *      change rarely), 0 stale for the permissions editor (always
 *      fresh when opened).
 *
 *   3. Write hooks (useCreateUserMutation, etc) — wrap useApiMutation
 *      with the agreed invalidation strategy. Callers don't have to
 *      remember which keys to invalidate.
 *
 * Invalidation rules from the audit:
 *   - Create user        → iamKeys.users()
 *   - Update user        → iamKeys.users() (cascades to detail)
 *   - Delete user        → iamKeys.users()
 *   - Reset password     → no display state changes
 *   - Update permissions → iamKeys.userPermissions(userName);
 *                          + profile() if userName is current user
 *   - Change my password → no display state changes
 */
import { useQueryClient } from '@tanstack/vue-query'
import { useApiQuery, useApiMutation } from '@/composables/useApi'
import { useAuthStore } from '@/stores/auth'
import {
  userApi,
  accountApi,
  type UserView,
  type UserSummary,
  type PermissionsView,
  type UserGroupSummary,
} from './api'
import type {
  UserCreateInput,
  UserUpdateInput,
  PasswordChangeInput,
  PasswordResetInput,
  PermissionsUpdateInput,
} from './schemas'

// -----------------------------------------------------------------
// Key catalogue
// -----------------------------------------------------------------

/**
 * Hierarchical query keys for the IAM domain. TanStack matches keys
 * by prefix on invalidate, so e.g. invalidating `users()` cascades
 * to every `userDetail(name)` and `userPermissions(name)` because
 * they share the prefix.
 */
export const iamKeys = {
  /** Root of the IAM domain. Invalidating here clears every IAM cache. */
  all: ['iam'] as const,

  /** All user-related queries. */
  users: () => [...iamKeys.all, 'users'] as const,

  /** Top-level user list. */
  userList: () => [...iamKeys.users(), 'list'] as const,

  /** One user's detail. */
  userDetail: (userName: string) =>
    [...iamKeys.users(), 'detail', userName] as const,

  /** One user's permissions map. */
  userPermissions: (userName: string) =>
    [...iamKeys.users(), 'permissions', userName] as const,

  /** Groups list (for the create-user picker). */
  groups: () => [...iamKeys.all, 'groups'] as const,

  /** Current user's profile. */
  profile: () => [...iamKeys.all, 'profile'] as const,
} as const

// -----------------------------------------------------------------
// Read hooks
// -----------------------------------------------------------------

/** List CMS admins. Stale 60 s — admin set changes rarely. */
export function useUsersQuery() {
  return useApiQuery<UserView[]>({
    queryKey: iamKeys.userList(),
    queryFn: () => userApi.list(),
    staleTime: 60_000,
  })
}

/** Fetch a single user. Lazily enabled — caller passes a ref. */
export function useUserQuery(userName: () => string | undefined) {
  return useApiQuery<UserView>({
    queryKey: [...iamKeys.users(), 'detail', userName()] as readonly unknown[],
    queryFn: () => userApi.get(userName()!),
    enabled: !!userName(),
    staleTime: 30_000,
  })
}

/**
 * Fetch a user's permissions map. staleTime: 0 because the editor
 * should reflect the latest authoritative state every time it opens
 * — privileges can be granted out-of-band by another admin.
 */
export function usePermissionsQuery(userName: () => string | undefined) {
  return useApiQuery<PermissionsView>({
    queryKey: [
      ...iamKeys.users(),
      'permissions',
      userName(),
    ] as readonly unknown[],
    queryFn: () => userApi.getPermissions(userName()!),
    enabled: !!userName(),
    staleTime: 0,
  })
}

/**
 * List user groups. Stale 5 minutes — group definitions change
 * very rarely.
 */
export function useGroupsQuery() {
  return useApiQuery<UserGroupSummary[]>({
    queryKey: iamKeys.groups(),
    queryFn: () => userApi.listGroups(),
    staleTime: 5 * 60_000,
  })
}

/**
 * Current user's profile. Used by the account screen and any UI that
 * needs live-refreshable privilege state (the auth store snapshot is
 * frozen at login). Stale 30 s.
 */
export function useProfileQuery() {
  return useApiQuery<UserSummary>({
    queryKey: iamKeys.profile(),
    queryFn: () => accountApi.profile(),
    staleTime: 30_000,
  })
}

// -----------------------------------------------------------------
// Write hooks
// -----------------------------------------------------------------

export function useCreateUserMutation() {
  const qc = useQueryClient()
  return useApiMutation<UserView, UserCreateInput>({
    mutationFn: (input: UserCreateInput) => userApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: iamKeys.users() })
    },
  })
}

export function useUpdateUserMutation(userName: string) {
  const qc = useQueryClient()
  return useApiMutation<UserView, UserUpdateInput>({
    mutationFn: (input: UserUpdateInput) => userApi.update(userName, input),
    onSuccess: () => {
      // Invalidates list AND any open detail panels for this user.
      qc.invalidateQueries({ queryKey: iamKeys.users() })
    },
  })
}

export function useDeleteUserMutation(userName: string) {
  const qc = useQueryClient()
  return useApiMutation<void, void>({
    mutationFn: () => userApi.delete(userName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: iamKeys.users() })
    },
  })
}

export function useResetPasswordMutation(userName: string) {
  // No invalidation — passwords aren't displayed anywhere.
  return useApiMutation<void, PasswordResetInput>({
    mutationFn: (input: PasswordResetInput) => userApi.resetPassword(userName, input),
  })
}

/**
 * Update permissions. Two invalidations:
 *   - The target user's permissions map (so the editor refreshes
 *     even if reopened immediately).
 *   - The current user's profile, IF the target is the current user.
 *     This makes the auth-store snapshot stale and forces a re-fetch
 *     so route guards reflect the new privileges.
 */
export function useUpdatePermissionsMutation(userName: string) {
  const qc = useQueryClient()
  const auth = useAuthStore()
  return useApiMutation<void, PermissionsUpdateInput>({
    mutationFn: (input: PermissionsUpdateInput) => userApi.updatePermissions(userName, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [
          ...iamKeys.users(),
          'permissions',
          userName,
        ] as readonly unknown[],
      })
      if (auth.user?.userName === userName) {
        qc.invalidateQueries({ queryKey: iamKeys.profile() })
      }
    },
  })
}

export function useChangeMyPasswordMutation() {
  // No invalidation — password change is fire-and-forget.
  return useApiMutation<void, PasswordChangeInput>({
    mutationFn: (input: PasswordChangeInput) => accountApi.changeMyPassword(input),
  })
}
